/** One pending AI change (backend metaplan A8, M24 rev 3): who asked, through which client, why,
 *  and — for a person holding `console_notification_mcp_write` — Apply / Reject. Asked over MCP, or
 *  by the console agent (A9: it proposes only, so every apply it asks for waits here).
 *
 *  Nothing has changed while it is pending: the MCP client cannot apply it on a production
 *  tenant, whatever it was told. Applying re-verifies what the requester saw (the preview's
 *  receipt, or the revert plan's digest); when the notifications moved since, the backend refuses
 *  (`stale`) and the requester has to preview again. A revert runs as a background revert.
 *
 *  A revert's card shows what the requester saw — the plan and, for a content batch, one line per
 *  record it reverts (`ReviewSummarySections`); an apply's preview is its proposal page's. An open
 *  change the viewer may not decide says what it needs (client MCP WPS FE-07: a content or
 *  vocabulary change also needs the console permission of every kind it touches). */
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import { aiChangeKeys, shortId } from '@/api/aiChanges';
import {
  aiEditKeys,
  applyPending,
  CANNOT_DECIDE_TEXT,
  decisionErrorText,
  pendingStatusColor,
  pendingStatusLabel,
  rejectPending,
  type AIPendingChange,
} from '@/api/aiEdit';
import { ConsoleApiError } from '@/api/client';
import { Field, FieldGrid } from '@/components/ReadView';
import { useSnackbar } from '@/components/Snackbar';
import { formatDate } from '@/utils/format';

import { batchHref } from './aiChangeLinks';
import { ReviewSummarySections } from './ReviewSummarySections';
import { REQUEST_REASON_LABEL, ReasonValue } from './theirWords';

type Decision = 'apply' | 'reject';

function errorText(e: unknown): string {
  if (e instanceof ConsoleApiError) {
    const body = e.body as { code?: string; detail?: string } | undefined;
    // P7: a copy of a franchise parent's notifications needs parent-level access (never for ops);
    // P3-16: a content or vocabulary change needs each of its kinds' console permissions.
    if (
      e.status === 403 &&
      (body?.code === 'parent_level_required' || body?.code === 'permission_required') &&
      body.detail
    ) {
      return body.detail;
    }
    if (e.status === 403) return 'Deciding needs the console_notification_mcp_write permission.';
    return decisionErrorText(body?.code, body?.detail ?? e.message);
  }
  return e instanceof Error ? e.message : String(e);
}

/** "Apply requested by the console agent" / "Apply requested over MCP" / "Revert requested over MCP"
 *  / "Revert of a whole batch requested over MCP". */
export function pendingTitle(pending: AIPendingChange): string {
  const what =
    pending.kind === 'apply' ? 'Apply' : pending.batch_id ? 'Revert of a whole batch' : 'Revert';
  return pending.channel === 'console_agent'
    ? `${what} requested by the console agent`
    : `${what} requested over MCP`;
}

export function PendingChangeCard({
  pending,
  withSummary,
}: {
  pending: AIPendingChange;
  /** Show what the requester saw. Default: for a revert (an apply's preview is its proposal page's). */
  withSummary?: boolean;
}) {
  const queryClient = useQueryClient();
  const snackbar = useSnackbar();
  const [decision, setDecision] = useState<Decision | null>(null);
  const [note, setNote] = useState('');

  const mutation = useMutation({
    mutationFn: (d: Decision) =>
      d === 'apply' ? applyPending(pending.id, { note }) : rejectPending(pending.id, { note }),
    onSuccess: (_result, d) => {
      setDecision(null);
      setNote('');
      void queryClient.invalidateQueries({ queryKey: aiEditKeys.all });
      void queryClient.invalidateQueries({ queryKey: aiChangeKeys.all });
      snackbar.show(
        d === 'reject'
          ? 'Rejected — nothing was changed.'
          : pending.kind === 'revert'
            ? 'The revert started; its outcome shows on the change set.'
            : 'Applied as a change set.',
        'success',
      );
    },
  });

  const requester = pending.requested_by?.email || 'an unknown user';
  const showSummary = withSummary ?? pending.kind === 'revert';
  // A content or vocabulary change: records, one change set per record for content.
  const recordsDomain =
    pending.summary?.domain === 'content' || pending.summary?.domain === 'vocabulary';
  return (
    <Box
      data-pending-change={pending.id}
      sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 2, mb: 1.5 }}
    >
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
        <Chip
          size="small"
          color={pendingStatusColor(pending.status)}
          label={pendingStatusLabel(pending.status)}
          data-pending-status
        />
        <Typography variant="subtitle2">
          {pendingTitle(pending)}
        </Typography>
        <Box sx={{ flex: 1 }} />
        {pending.can_decide && (
          <>
            <Button
              size="small"
              variant="contained"
              startIcon={<CheckIcon />}
              onClick={() => setDecision('apply')}
            >
              Apply…
            </Button>
            <Button
              size="small"
              variant="outlined"
              color="inherit"
              startIcon={<CloseIcon />}
              onClick={() => setDecision('reject')}
            >
              Reject…
            </Button>
          </>
        )}
      </Stack>
      <FieldGrid>
        <Field
          label="Requested by"
          value={`${requester} (${pending.requester_kind === 'ops' ? 'ops' : 'tenant staff'})`}
        />
        <Field
          label="Client"
          value={
            pending.channel === 'console_agent'
              ? 'Console agent'
              : pending.oauth_client_name || pending.oauth_client_id || '—'
          }
        />
        <Field label="When" value={pending.created_ts ? formatDate(pending.created_ts) : '—'} />
        <Field label="Request IP" value={pending.request_ip || '—'} />
        <Field label="Sign-in IP" value={pending.login_ip || '—'} />
        <Field label={REQUEST_REASON_LABEL} value={<ReasonValue text={pending.reason} />} />
        {pending.decided_by && (
          <Field
            label="Decided by"
            value={`${pending.decided_by.email}${pending.decided_ts ? ` · ${formatDate(pending.decided_ts)}` : ''}${
              pending.decision_note ? ` — ${pending.decision_note}` : ''
            }`}
          />
        )}
        {pending.batch_id && (
          <Field
            label="Batch"
            value={
              <>
                <Box component="span" sx={{ fontFamily: 'monospace' }}>
                  {shortId(pending.batch_id)}
                </Box>{' '}
                ·{' '}
                <RouterLink to={batchHref(pending.batch_id)}>
                  All its change sets
                </RouterLink>
              </>
            }
          />
        )}
        {pending.result_change_set_id && (
          <Field
            label="Change set"
            value={
              <RouterLink to={`/ai-changes/${pending.result_change_set_id}`}>
                {pending.result_change_set_id.slice(0, 8)}
              </RouterLink>
            }
          />
        )}
      </FieldGrid>
      {!pending.can_decide && pending.status === 'pending' && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }} data-cannot-decide>
          {CANNOT_DECIDE_TEXT}
        </Typography>
      )}
      {pending.error && (
        <Alert severity={pending.status === 'stale' ? 'info' : 'error'} sx={{ mt: 1 }}>
          {pending.error}
        </Alert>
      )}
      {showSummary && (
        <Box sx={{ mt: 1.5 }}>
          <ReviewSummarySections summary={pending.summary} bare />
        </Box>
      )}

      <Dialog open={decision !== null} onClose={() => setDecision(null)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {decision === 'apply' ? 'Apply this change?' : 'Reject this change?'}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            {decision === 'apply'
              ? pending.kind === 'apply'
                ? recordsDomain
                  ? 'It applies exactly what was previewed, as revertible change sets — or nothing, if the records changed since.'
                  : 'It applies exactly what was previewed, as one revertible change set — or nothing, if the notifications changed since.'
                : pending.batch_id
                  ? 'It reverts every change set of the batch with the plan the requester saw, in one transaction — or nothing, if the records changed since.'
                  : 'It reverts the change set with the plan the requester saw — or nothing, if the records changed since.'
              : 'Nothing is changed; the requester sees that it was rejected.'}
          </Typography>
          <TextField
            label="Note (optional)"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            fullWidth
            multiline
            minRows={2}
          />
          {mutation.isError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {errorText(mutation.error)}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDecision(null)} disabled={mutation.isPending}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color={decision === 'apply' ? 'primary' : 'inherit'}
            disabled={mutation.isPending}
            onClick={() => decision && mutation.mutate(decision)}
          >
            {decision === 'apply' ? 'Apply' : 'Reject'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
